<template>
  <div class="buxida-wysiwyg-editor">
    <div class="buxida-wysiwyg-editor__actions">
      <b-button
        size="sm"
        variant="outline-secondary"
        :disabled="disabled"
        @click="showLibrary = true"
      >
        <feather-icon icon="ImageIcon" /> Bibliothèque d'images
      </b-button>
      <span
        v-if="uploading"
        class="ml-2 text-muted small"
      >
        <b-spinner small /> Upload en cours…
      </span>
    </div>

    <editor
      ref="toastEditor"
      :initial-value="value"
      :options="editorOptions"
      :initial-edit-type="initialEditType"
      :height="height"
      preview-style="vertical"
      @change="onEditorChange"
    />

    <image-library-modal
      :visible.sync="showLibrary"
      @insert="onInsertImage"
      @error="onLibraryError"
    />
  </div>
</template>

<script>
// Wrapper Toast UI Editor pour l'edition des propositions de documentation.
// Markdown-natif (pas de round-trip HTML), offre 2 modes (Markdown source
// vs WYSIWYG) togglables via la toolbar du composant Toast UI lui-meme.
//
// Pourquoi pas Tiptap : Tiptap v1 (la seule version Vue 2) est ancien
// (2020-21), et le round-trip ProseMirror <-> Markdown requiert un
// serializer custom qui n'est pas idempotent par defaut (faux positifs
// sur dirty flag + perte de fidelite sur le HTML inline).
// Toast UI conserve le Markdown comme source de verite a chaque sauvegarde.
//
// API identique a MarkdownEditor.vue pour swap transparent cote parent :
//   props   : value (v-model), height, placeholder, disabled
//   events  : @input (markdown), @library-error

import { BButton, BSpinner } from 'bootstrap-vue'
import axios from '@axios'
import ImageLibraryModal from '@/views/dashboard/docs/ImageLibraryModal.vue'

// Charge Toast UI Editor en lazy : si le module n'est pas installe, le
// composant rend une erreur explicite plutot que de casser DocsDetail.
let ToastEditor = null
try {
  // eslint-disable-next-line global-require
  const mod = require('@toast-ui/vue-editor')
  ToastEditor = mod.Editor
  // eslint-disable-next-line global-require
  require('@toast-ui/editor/dist/toastui-editor.css')
} catch (e) {
  // Dep absente : le composant rend une placeholder (cf. components.editor).
}

const DOCS_API_BASE = 'https://api2.buxida.com'

const IMG_PLACEHOLDER = 'data:image/svg+xml;base64,'
  + 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiLz4='

export default {
  name: 'WysiwygEditor',
  components: {
    BButton,
    BSpinner,
    ImageLibraryModal,
    // Composant Toast UI charge en lazy. Si l'import a echoue (dep
    // manquante), on rend une placeholder a la place.
    editor: ToastEditor || {
      props: ['initialValue', 'options', 'initialEditType', 'height'],
      template: `<div class="text-danger p-3 border rounded">
        Composant Toast UI Editor non disponible (la dépendance
        <code>@toast-ui/vue-editor</code> n'est pas installée ou a
        échoué à charger). Bascule sur l'éditeur Markdown classique.
      </div>`,
    },
  },
  props: {
    value: { type: String, default: '' },
    height: { type: String, default: '60vh' },
    placeholder: { type: String, default: 'Écrire la documentation…' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      showLibrary: false,
      uploading: 0,
      imageBlobs: {}, // name -> objectURL
      imageBlobsLoading: {},
      mutationObserver: null,
      // Mode initial : WYSIWYG (l'admin verra rarement du markdown brut).
      // Le user peut basculer en mode "markdown" via la toolbar Toast UI.
      initialEditType: 'wysiwyg',
      editorOptions: {
        usageStatistics: false,
        // Toast UI Editor v2 attend un tableau PLAT de noms d'items
        // (avec 'divider' pour separer les groupes). Le format imbrique
        // est arrive en v3, qui n'est pas compatible Vue 2.
        toolbarItems: [
          'heading', 'bold', 'italic', 'strike',
          'divider',
          'hr', 'quote',
          'divider',
          'ul', 'ol', 'task', 'indent', 'outdent',
          'divider',
          'table', 'link',
          'divider',
          'code', 'codeblock',
        ],
        customHTMLRenderer: {
          // Reecrit les images _images/<name> avec un placeholder qu'on
          // va swapper en blob authentifie au prochain tick.
          image: node => {
            const src = node.destination || ''
            const m = /^_images\/([a-zA-Z0-9._-]+)$/.exec(src)
            if (!m) {
              return {
                type: 'openTag',
                tagName: 'img',
                attributes: { src, alt: node.firstChild ? node.firstChild.literal : '' },
                selfClose: true,
              }
            }
            return {
              type: 'openTag',
              tagName: 'img',
              attributes: {
                src: IMG_PLACEHOLDER,
                'data-buxida-image': m[1],
                alt: node.firstChild ? node.firstChild.literal : '',
              },
              selfClose: true,
            }
          },
        },
        hooks: {
          // Drag-drop / paste image : uploade via /admin/docs/images,
          // puis remplace l'URL externe par notre `_images/<name>` pour
          // que la source Markdown reste relative au repo.
          addImageBlobHook: this.handleImageBlobHook,
        },
      },
    }
  },
  computed: {
    toastUnavailable() {
      return !ToastEditor
    },
  },
  mounted() {
    if (this.toastUnavailable) return
    // Apres le premier rendu, hookons un MutationObserver sur le DOM de
    // l'editeur pour swapper les <img data-buxida-image="..."> vers
    // leur blob URL authentifiee.
    this.$nextTick(() => this.attachObserver())
  },
  beforeDestroy() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
    this.revokeImageBlobs()
  },
  methods: {
    onEditorChange() {
      if (this.toastUnavailable) return
      const md = this.invokeEditor('getMarkdown')
      if (typeof md === 'string' && md !== this.value) {
        this.$emit('input', md)
      }
    },
    invokeEditor(method, ...args) {
      const ref = this.$refs.toastEditor
      if (!ref) return null
      // Toast UI Vue wrapper expose `invoke` pour appeler des methodes
      // de l'instance editor sous-jacente.
      try {
        if (typeof ref.invoke === 'function') return ref.invoke(method, ...args)
        // fallback : acces direct (selon version du wrapper)
        if (ref.editor && typeof ref.editor[method] === 'function') {
          return ref.editor[method](...args)
        }
        if (ref.editorInst && typeof ref.editorInst[method] === 'function') {
          return ref.editorInst[method](...args)
        }
      } catch (_) { /* swallow */ }
      return null
    },
    onLibraryError(message) {
      this.$emit('library-error', message)
    },
    onInsertImage(item) {
      // Insertion programmatique de `![alt](_images/<name>)` au curseur.
      const alt = this.altFromName(item.name)
      const snippet = `![${alt}](_images/${item.name})`
      this.invokeEditor('insertText', snippet)
    },
    altFromName(name) {
      return String(name).replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ')
    },
    async handleImageBlobHook(blob, callback) {
      this.uploading += 1
      try {
        const form = new FormData()
        const filename = blob.name || `image-${Date.now()}.png`
        form.append('file', blob, filename)
        const response = await axios.post(`${DOCS_API_BASE}/admin/docs/images`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const data = response.data || {}
        if (data.res !== 1 || !data.item) {
          throw new Error((data && data.detail) || 'Upload échoué')
        }
        // Toast UI s'attend a recevoir (url, altText) — on lui passe la
        // ref _images/<name> pour que le Markdown reste relatif au repo.
        callback(`_images/${data.item.name}`, this.altFromName(data.item.name))
      } catch (err) {
        const msg = (err && err.response && err.response.data && err.response.data.detail)
          || (err && err.message)
          || 'Erreur inconnue'
        this.$emit('library-error', `Upload ${blob && blob.name} : ${msg}`)
      } finally {
        this.uploading -= 1
      }
    },
    attachObserver() {
      const root = this.$el
      if (!root) return
      const obs = new MutationObserver(() => this.rewriteImageSrcs())
      obs.observe(root, { childList: true, subtree: true, attributes: false })
      this.mutationObserver = obs
      this.rewriteImageSrcs()
    },
    rewriteImageSrcs() {
      const imgs = this.$el.querySelectorAll('img[data-buxida-image]')
      imgs.forEach(img => {
        const name = img.getAttribute('data-buxida-image')
        if (!name) return
        const cached = this.imageBlobs[name]
        if (cached) {
          if (img.getAttribute('src') !== cached) img.setAttribute('src', cached)
          return
        }
        this.fetchImageBlob(name).then(url => {
          if (url) img.setAttribute('src', url)
        })
      })
    },
    fetchImageBlob(name) {
      if (this.imageBlobs[name]) return Promise.resolve(this.imageBlobs[name])
      if (this.imageBlobsLoading[name]) return this.imageBlobsLoading[name]
      const promise = axios
        .get(`${DOCS_API_BASE}/admin/docs/images/static/${encodeURIComponent(name)}`, {
          responseType: 'blob',
        })
        .then(resp => {
          const url = URL.createObjectURL(resp.data)
          this.$set(this.imageBlobs, name, url)
          delete this.imageBlobsLoading[name]
          return url
        })
        .catch(() => {
          delete this.imageBlobsLoading[name]
          return null
        })
      this.imageBlobsLoading[name] = promise
      return promise
    },
    revokeImageBlobs() {
      Object.values(this.imageBlobs).forEach(url => {
        try { URL.revokeObjectURL(url) } catch (_) { /* ignore */ }
      })
      this.imageBlobs = {}
      this.imageBlobsLoading = {}
    },
  },
}
</script>

<style scoped>
.buxida-wysiwyg-editor__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.buxida-wysiwyg-editor ::v-deep .toastui-editor-defaultUI {
  border: 1px solid #e3e3e3;
  border-radius: 4px;
}
.buxida-wysiwyg-editor ::v-deep .toastui-editor-md-container,
.buxida-wysiwyg-editor ::v-deep .toastui-editor-ww-container {
  background: #fff;
}
</style>
